/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from "@ember/object";
import type RouterService from "@ember/routing/router-service";
import type Transition from "@ember/routing/transition";
import { service } from "@ember/service";
import { tracked } from "@glimmer/tracking";
import type { ValidatorMap } from "ember-changeset/types";
import type { TaskInstance } from "ember-concurrency";
import { restartableTask, timeout } from "ember-concurrency";
import type IntlService from "ember-intl/services/intl";
import CrudJournalEventBaseComponent from "parog-web/components/forc/abstract/crud-journal-event-based-component";
import JournalEventRetrievalStrategies from "parog-web/components/forc/utils/journal-event-retrieval-strategies";
import type CareGiver from "parog-web/models/care-giver";
import CareGiverAddedJournalEvent, {
  CareGiverRoles
} from "parog-web/models/care-giver-added-journal-event";
import type SemeiaSessionService from "parog-web/services/session";
// eslint-disable-next-line ember/use-ember-data-rfc-395-imports
import type DS from "ember-data";

import type { EmberChangeset } from "ember-changeset";
import { validatePresence } from "ember-changeset-validations/validators";
import type FetcherService from "parog-web/services/fetcher";
import validateFrenchPhoneNumber from "parog-web/validators/french-phone-number";
import validateSometimes from "parog-web/validators/sometimes";
import styles from "./index.module.css";

export default class NewCareGiverFormComponent extends CrudJournalEventBaseComponent {
  styles = styles;

  @service declare session: SemeiaSessionService;
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare fetcher: FetcherService;

  @tracked
  careGiverSelected = false;

  @tracked
  searchedBy?: string;

  static forcDisplayName = "Nouveau membre de l'entourage de soin";
  static fullComponentId = "care-giver/new";

  static journalEventClass = CareGiverAddedJournalEvent;
  retrieveJournalEvent = JournalEventRetrievalStrategies.sourceJournalEvent;

  get validations(): ValidatorMap {
    return Object.assign({}, this.journalEventClass.validations(this.intl), {
      phone: validateSometimes(
        [
          validatePresence({
            presence: true,
            message: "Le numéro de téléphone ne peux être vide"
          }),
          validateFrenchPhoneNumber({
            message: "Le numéro de téléphone n'est pas valide"
          })
        ],
        function (this: EmberChangeset) {
          return (this as EmberChangeset).get("phone");
        }
      )
    });
  }

  roleOptions = CareGiverRoles;

  afterCancel = (): Transition => {
    const targetRoute = this.session.patient
      ? "patient-web.protected.consult.medical-synthesis"
      : "medics-web.protected.patients.patient.administrative";

    return this.router.replaceWith(targetRoute);
  };

  afterCompleted = (): Transition => {
    const targetRoute = this.session.patient
      ? "patient-web.protected.consult.medical-synthesis"
      : "medics-web.protected.patients.patient.administrative";

    return this.router.replaceWith(targetRoute);
  };

  queryCareGivers = restartableTask(async (property: string, event: Event) => {
    this.careGiverSelected = false;
    this.searchedBy = property;

    const query = (event.target as HTMLInputElement).value;
    if (query.length < 1) return [];

    // Bounce search
    await timeout(400);

    const filterObject = {
      health_centre_id: this.args.patient.healthCentre.id
    };
    if (property == "lastName") {
      Object.assign(filterObject, { last_name: query });
    } else {
      Object.assign(filterObject, { first_name: query });
    }

    const careGivers = await this.store.query("care-giver", {
      filter: filterObject
    });

    return careGivers;
  });

  @action
  onCareGiverSelected(index: string) {
    this.careGiverSelected = true;
    this.searchedBy = undefined;

    const careGiver = (
      (
        this.queryCareGivers.lastSuccessful as TaskInstance<
          DS.RecordArray<CareGiver>
        >
      ).value as DS.RecordArray<CareGiver>
    )[index];

    this.changeset.set("lastName", careGiver.lastName);
    this.changeset.set("firstName", careGiver.firstName);
    this.changeset.set("role", careGiver.role);
    this.changeset.set("email", careGiver.email);
    this.changeset.set("phone", careGiver.phone);
    this.changeset.set("address", careGiver.address);
    this.changeset.set("comments", careGiver.comments);
  }

  get shouldDisplaySuggestions() {
    return (
      !this.careGiverSelected && this.queryCareGivers.lastSuccessful?.value
    );
  }

  async save(): Promise<CareGiverAddedJournalEvent> {
    const je = await super.save();

    // We make sure we get the computed referent-lab
    // which is executed by PR-API every time
    // we save a care giver which may be a referent laboratory
    if (je.isValidLaboratory) {
      await this.fetcher.query("referent-lab-updated-journal-event", {
        filter: {
          patient_id: this.args.patient.id
        }
      });
    }

    return je;
  }
}
